import { useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';

const KEY = 'uiq_last_activity';

export default function useIdleLogout({ enabled, timeoutMs = 30 * 60 * 1000, onLogout }) {
  const timer = useRef(null);

  useEffect(() => {
    const read = () => { try { return Number(localStorage.getItem(KEY)) || 0; } catch { return 0; } };
    const write = () => { try { localStorage.setItem(KEY, String(Date.now())); } catch {} };
    const clear = () => { try { localStorage.removeItem(KEY); } catch {} };

    if (!enabled) { clear(); return; }

    const logout = async () => {
      clearTimeout(timer.current);
      clear();
      await supabase.auth.signOut({ scope: 'local' });
      if (onLogout) onLogout();
    };
    const schedule = () => {
      clearTimeout(timer.current);
      const remaining = timeoutMs - (Date.now() - (read() || Date.now()));
      if (remaining <= 0) { logout(); return; }
      timer.current = setTimeout(check, remaining + 250);
    };
    const check = () => {
      const last = read();
      if (last && Date.now() - last >= timeoutMs) logout();
      else schedule();
    };

    // If the tab/app was closed past the limit, sign out on return
    const last = read();
    if (last && Date.now() - last >= timeoutMs) { logout(); return; }
    write();
    schedule();

    let lastTick = 0;
    const onActivity = () => {
      const t = Date.now();
      if (t - lastTick < 5000) return;
      lastTick = t;
      write();
      schedule();
    };
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    const onStorage = (e) => { if (e.key === KEY) schedule(); };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('storage', onStorage);

    return () => {
      clearTimeout(timer.current);
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('storage', onStorage);
    };
  }, [enabled, timeoutMs, onLogout]);
}